import { motion } from "framer-motion";
import { Clock, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScheduleSessionSurface } from "./ScheduleSessionSurface";
import { scheduleSlotsHeigthMobile } from "@/utils/scheduleUtils";
import { cn } from "@/lib/utils";
import type { ScheduledClass } from "@/types/types";

/** Pure staff timeline presentation; the caller retains attendee permissions and data access. */
export function ScheduleTimelineCard<T extends ScheduledClass & { booked_count: number }>({
  klass, onOpenAttendees, slotDuration, startTime, columnIndex = 0, totalColumns = 1,
}: {
  klass: T;
  onOpenAttendees: (klass: T) => void;
  slotDuration: number;
  startTime: string;
  columnIndex?: number;
  totalColumns?: number;
}) {
  const [h, m] = klass.time.split(":").map(Number);
  const [bh, bm] = startTime.split(":").map(Number);
  const pxPerMinute = scheduleSlotsHeigthMobile / slotDuration;
  const top = (h * 60 + m - bh * 60 - bm) * pxPerMinute;
  const height = Math.max(scheduleSlotsHeigthMobile, Number.parseInt(klass.duration || "45") * pxPerMinute - 8);
  const full = klass.booked_count >= klass.capacity;
  const remaining = klass.capacity ? ((klass.capacity - klass.booked_count) / klass.capacity) * 100 : 0;
  const tone = full ? "full" : remaining >= 60 ? "available" : remaining >= 40 ? "limited" : "nearly-full";
  const width = 100 / totalColumns;
  const compact = totalColumns > 1;

  return (
    <ScheduleSessionSurface>
      <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
        style={{ top, height, left: `calc(${columnIndex * width}% + 4px)`, width: `calc(${width}% - 8px)` }}
        className={cn("coach-day-class absolute rounded-md border shadow-sm flex flex-col justify-between", `schedule-capacity-${tone}`, compact ? "p-2" : "p-3")}
        onClick={() => onOpenAttendees(klass)} role="button" tabIndex={0}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpenAttendees(klass); } }}
        data-capacity-tone={tone}>
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0 flex-1">
            <div className={cn("font-semibold leading-tight truncate", compact ? "text-xs" : "text-sm")}>{klass.name}</div>
            <div className="text-xs opacity-90 flex items-center gap-1"><Clock className="h-3 w-3 shrink-0" />{klass.time} · {klass.duration}m</div>
          </div>
          <Badge className="coach-day-capacity shrink-0 rounded-full" variant={full ? "default" : "secondary"}>
            <Users className="mr-1 h-3 w-3" />{klass.booked_count}/{klass.capacity}
          </Badge>
        </div>
      </motion.div>
    </ScheduleSessionSurface>
  );
}